'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useMenuRoleSetList } from '@/hooks/useMenuRoleSet';
import { apiClient } from '@/lib/api-client';
import { menuRoleSetClient } from '@/lib/menu-role-set-client';

import {
  AdminUsersResponse,
  Organization,
  PetrohqApiResponsePagedModelAdminUsersResponse,
} from '@/lib/api/data-contracts';
import {
  Building2,
  CheckCircle,
  Edit,
  Loader2,
  Mail,
  MoreHorizontal,
  Phone,
  RefreshCw,
  Trash2,
  User,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

// 타입 별칭 정의
type UserType = AdminUsersResponse;
type OrganizationType = Organization;

interface UserFormData {
  loginId: string;
  username: string;
  organizationCode: string;
  phoneNumber: string;
  email: string;
  jobPosition: string;
  jobTitle: string;
}

export function UserManagement() {
  const { toast } = useToast();
  const { data: roleSetData } = useMenuRoleSetList();
  const roleSets = roleSetData ?? [];
  // 현재 값은 목록 응답의 roleSetId에서 가져오고(Part A),
  // 변경 직후 재조회 전까지는 로컬 상태로 낙관적 반영한다.
  const [assignedRoleSet, setAssignedRoleSet] = useState<Record<number, string>>({});
  const [users, setUsers] = useState<UserType[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchCategory, setSearchCategory] = useState('username');
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [filteredUsers, setFilteredUsers] = useState<UserType[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const [formData, setFormData] = useState<UserFormData>({
    loginId: '',
    username: '',
    organizationCode: '',
    phoneNumber: '',
    email: '',
    jobPosition: '',
    jobTitle: '',
  });

  // 사용자 목록 조회
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('admin/user', {
        searchParams: {
          page: 0,
          size: 1000,
        },
      });
      const data = (await response.json()) as PetrohqApiResponsePagedModelAdminUsersResponse;
      const userData = data.result?.content || [];
      setUsers(userData);
      setFilteredUsers(userData);
    } catch {
      toast({
        title: '사용자 목록 조회 실패',
        description: '사용자 목록을 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // 조직 목록 조회
  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await apiClient.get('admin/organization');
      const data = (await response.json()) as OrganizationType[];
      setOrganizations(data || []);
    } catch {
      toast({
        title: '조직 목록 조회 실패',
        description: '조직 목록을 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    }
  }, [toast]);

  // 초기 데이터 로드
  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
  }, [fetchOrganizations, fetchUsers]);

  // 실시간 검색 기능 (디바운스 적용)
  const handleSearch = useCallback(() => {
    if (!searchValue.trim()) {
      setFilteredUsers(users);
      return;
    }

    const filtered = users.filter(user => {
      const searchTarget = getSearchValue(user, searchCategory);
      return searchTarget.toLowerCase().includes(searchValue.toLowerCase());
    });
    setFilteredUsers(filtered);
  }, [searchValue, searchCategory, users]);

  // 사용자 이니셜 생성
  const getUserInitials = (name: string) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // 검색 대상 값 가져오기
  const getSearchValue = (user: UserType, category: string): string => {
    switch (category) {
      case 'organizationName':
        return user.orgName || '';
      case 'loginId':
        return user.loginId || '';
      case 'username':
        return user.username || '';
      case 'email':
        return user.email || '';
      case 'jobPosition':
        return user.jobPosition || '';
      case 'jobTitle':
        return user.jobTitle || '';
      default:
        return '';
    }
  };

  // 폼 유효성 검사
  const validateForm = (data: UserFormData) => {
    const errors: string[] = [];

    if (!data.loginId.trim()) {
      errors.push('사용자 ID는 필수입니다');
    } else if (data.loginId.length < 3) {
      errors.push('사용자 ID는 3자 이상이어야 합니다');
    }

    if (!data.username.trim()) {
      errors.push('이름은 필수입니다');
    }

    if (!data.organizationCode) {
      errors.push('소속 조직을 선택해주세요');
    }

    if (data.email && !/\S+@\S+\.\S+/.test(data.email)) {
      errors.push('올바른 이메일 형식을 입력해주세요');
    }

    if (errors.length > 0) {
      toast({
        title: '입력 오류',
        description: errors.join('\n'),
        variant: 'destructive',
      });
    }

    return errors.length === 0;
  };

  // 사용자 편집
  const handleEditUser = (user: UserType) => {
    setSelectedUser(user);
    setFormData({
      loginId: user.loginId || '',
      username: user.username || '',
      organizationCode: user.orgCd || '',
      phoneNumber: user.phoneNumber || '',
      email: user.email || '',
      jobPosition: user.jobPosition || '',
      jobTitle: user.jobTitle || '',
    });
    setEditDialogOpen(true);
  };

  // 사용자 정보 저장
  const handleSaveUser = async () => {
    if (!selectedUser) return;

    if (!validateForm(formData)) {
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.put(`admin/user/${selectedUser.userId}`, {
        json: formData,
      });

      toast({
        title: '저장 완료',
        description: '사용자 정보가 성공적으로 저장되었습니다',
        variant: 'default',
      });

      setEditDialogOpen(false);
      fetchUsers();
    } catch {
      toast({
        title: '저장 실패',
        description: '사용자 정보 저장 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 사용자 삭제/비활성화
  const handleDeleteUser = async (user: UserType) => {
    if (!user.userId) return;

    if (!confirm(`사용자 '${user.username || user.loginId}'를 비활성화하시겠습니까?`)) {
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.delete(`admin/user/${user.userId}`);

      toast({
        title: '삭제 완료',
        description: '사용자가 성공적으로 비활성화되었습니다',
        variant: 'default',
      });

      fetchUsers();
    } catch {
      toast({
        title: '삭제 실패',
        description: '사용자 삭제 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 역할세트 지정
  const handleAssignRoleSet = async (userId: number, roleSetId: string) => {
    try {
      await menuRoleSetClient.assignToUser(userId, Number(roleSetId));
      setAssignedRoleSet(prev => ({ ...prev, [userId]: roleSetId }));
      toast({
        title: '역할세트 지정 완료',
        description: '사용자의 역할세트가 변경되었습니다',
      });
      // 재조회로 서버 값을 반영하고 낙관적 로컬 값을 정리한다
      await fetchUsers();
      setAssignedRoleSet(prev => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    } catch (error) {
      toast({
        title: '역할세트 지정 실패',
        description: error instanceof Error ? error.message : '역할세트 지정 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    }
  };

  // 실시간 검색 적용
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch();
    }, 300); // 300ms 디바운스

    return () => clearTimeout(timeoutId);
  }, [handleSearch]);

  return (
    <ExpandableCard
      breadcrumb={
        <Breadcrumb items={menuBreadcrumbItems('user-management')} />
      }
      title='사용자 목록'
    >
      {/* Search Controls */}
      <div className='flex items-center justify-between gap-4'>
        <div className='flex items-center gap-2'>
          <Select value={searchCategory} onValueChange={setSearchCategory}>
            <SelectTrigger className='w-32'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='organizationName'>소속조직</SelectItem>
              <SelectItem value='loginId'>아이디</SelectItem>
              <SelectItem value='username'>이름</SelectItem>
              <SelectItem value='email'>이메일</SelectItem>
              <SelectItem value='jobPosition'>직위</SelectItem>
              <SelectItem value='jobTitle'>직책</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder='실시간 검색 (3글자 이상)'
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            className='w-64'
          />
          <div className='text-sm text-muted-foreground'>
            {filteredUsers.length}개 결과
          </div>
        </div>
      </div>

      {/* 사용자 테이블 */}
      <div className='border rounded-lg'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[200px]">사용자</TableHead>
              <TableHead className="min-w-[250px]">연락처</TableHead>
              <TableHead className="min-w-[160px]">조직</TableHead>
              <TableHead className="min-w-[120px]">직위/직책</TableHead>
              {/* w-48(192px)은 내부 Select(w-44=176px)+셀 패딩(32px)에 16px 모자라
                  브라우저가 다른 컬럼 폭을 뺏어 넓혔다. 실제 필요폭으로 맞춘다. */}
              <TableHead className="w-52">역할세트</TableHead>
              <TableHead className="text-right w-20">작업</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-3 w-16" />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <Skeleton className="h-3 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </TableCell>
                  <TableCell>
                    <Skeleton className='h-3 w-20' />
                  </TableCell>
                  <TableCell>
                    <Skeleton className='h-6 w-16' />
                  </TableCell>
                  <TableCell>
                    <Skeleton className='h-8 w-40' />
                  </TableCell>
                  <TableCell className="text-right">
                    <Skeleton className='h-8 w-8 ml-auto' />
                  </TableCell>
                </TableRow>
              ))
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className='text-center py-12'>
                  <div className='flex flex-col items-center gap-2'>
                    <User className='h-8 w-8 text-muted-foreground' />
                    <div className='text-muted-foreground'>
                      {searchValue
                        ? '검색 결과가 없습니다'
                        : '사용자가 없습니다'}
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map(user => (
                <TableRow
                  key={user.userId}
                  className="transition-colors hover:bg-muted/50"
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs">
                          {getUserInitials(user.username || user.loginId || '')}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{user.username || user.loginId}</div>
                        <div className="text-sm text-gray-500">@{user.loginId}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {user.email && (
                        <div className="flex items-center gap-1 text-sm">
                          <Mail className="size-3" />
                          {user.email}
                        </div>
                      )}
                      {user.phoneNumber && (
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Phone className="size-3" />
                          {user.phoneNumber}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.orgName && (
                      <div className="flex items-center gap-1 text-sm">
                        <Building2 className="size-3" />
                        {user.orgName}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div>{user.jobPosition || '-'}</div>
                      <div className="text-gray-500">{user.jobTitle || '-'}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={
                        user.userId != null
                          ? assignedRoleSet[user.userId] ??
                            (user.roleSetId != null
                              ? String(user.roleSetId)
                              : '')
                          : ''
                      }
                      onValueChange={value => {
                        if (user.userId != null) {
                          handleAssignRoleSet(user.userId, value);
                        }
                      }}
                    >
                      <SelectTrigger className="w-44">
                        <SelectValue placeholder="역할세트 지정" />
                      </SelectTrigger>
                      <SelectContent>
                        {roleSets.map(rs => (
                          <SelectItem key={rs.id} value={String(rs.id)}>
                            {rs.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>작업</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => handleEditUser(user)}>
                          <Edit className="mr-2 h-4 w-4" />
                          편집
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => handleDeleteUser(user)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          삭제
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className='max-w-2xl'>
          <DialogHeader>
            <DialogTitle>사용자 정보 수정</DialogTitle>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='grid grid-cols-12 gap-4 items-center'>
              <Label className='col-span-2'>ID</Label>
              <Input
                value={formData.loginId}
                readOnly
                className='col-span-10 bg-gray-50'
              />
            </div>

            <div className='grid grid-cols-12 gap-4 items-center'>
              <Label className='col-span-2'>이름</Label>
              <Input
                value={formData.username}
                onChange={e =>
                  setFormData({ ...formData, username: e.target.value })
                }
                className='col-span-10'
                placeholder='홍길동'
              />
            </div>

            <div className='grid grid-cols-12 gap-4 items-center'>
              <Label className='col-span-2'>소속 조직</Label>
              <Select
                value={formData.organizationCode}
                onValueChange={value =>
                  setFormData({ ...formData, organizationCode: value })
                }
              >
                <SelectTrigger className='col-span-9'>
                  <SelectValue placeholder='조직을 선택하세요' />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map(org =>
                    org.orgCd ? (
                      <SelectItem key={org.orgCd} value={org.orgCd}>
                        {org.orgCd + ' ' + org.orgName}
                      </SelectItem>
                    ) : null
                  )}
                </SelectContent>
              </Select>
              <Button
                variant='outline'
                size='sm'
                onClick={fetchOrganizations}
                className='col-span-1'
              >
                <RefreshCw className='h-4 w-4' />
              </Button>
            </div>

            <div className='grid grid-cols-12 gap-4 items-center'>
              <Label className='col-span-2'>이메일</Label>
              <Input
                type='email'
                value={formData.email}
                onChange={e =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className='col-span-10'
                placeholder='email@example.com'
              />
            </div>

            <div className='grid grid-cols-12 gap-4 items-center'>
              <Label className='col-span-2'>직위</Label>
              <Input
                value={formData.jobPosition}
                onChange={e =>
                  setFormData({ ...formData, jobPosition: e.target.value })
                }
                className='col-span-10'
              />
            </div>

            <div className='grid grid-cols-12 gap-4 items-center'>
              <Label className='col-span-2'>직책</Label>
              <Input
                value={formData.jobTitle}
                onChange={e =>
                  setFormData({ ...formData, jobTitle: e.target.value })
                }
                className='col-span-10'
              />
            </div>

            <div className='flex justify-end gap-2 pt-4'>
              <Button
                variant='outline'
                onClick={() => setEditDialogOpen(false)}
                disabled={isSubmitting}
              >
                취소
              </Button>
              <Button onClick={handleSaveUser} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    저장 중...
                  </>
                ) : (
                  <>
                    <CheckCircle className='mr-2 h-4 w-4' />
                    저장
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </ExpandableCard>
  );
}
